import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminSidebar } from '@/components/admin/AdminSidebar';
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

const AdminAddStudentPage: React.FC = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    nickname: '',
    parent_tel: '',
    school: '',
  });

  const generateNextStudentCode = async () => {
    try {
      const { data, error } = await (supabase as any)
        .schema("student")
        .from('fishsix_students')
        .select('code')
        .like('code', 'fs_%')
        .order('code', { ascending: false })
        .limit(10);

      if (error) throw error;

      if (!data || data.length === 0) {
        return 'fs_0001';
      }

      // กรองเฉพาะ code ที่เป็นรูปแบบ fs_xxxx (4 หลัก)
      const validCodes = data
        .map(item => item.code)
        .filter(code => /^fs_\d{4}$/.test(code))
        .sort()
        .reverse();

      if (validCodes.length === 0) {
        return 'fs_0001';
      }

      const lastCode = validCodes[0];
      const numberPart = lastCode.substring(3); // Remove "fs_" prefix
      const parsedNumber = parseInt(numberPart, 10);
      
      if (isNaN(parsedNumber)) {
        console.error('Invalid number part in code:', numberPart);
        return 'fs_0001';
      }
      
      const nextNumber = parsedNumber + 1;
      return `fs_${nextNumber.toString().padStart(4, '0')}`;
    } catch (error) {
      console.error('Error generating student code:', error);
      throw error;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const code = await generateNextStudentCode();
      
      const { error } = await (supabase as any)
        .schema("student")
        .from('fishsix_students')
        .insert([{
          code,
          first_name: formData.first_name,
          last_name: formData.last_name,
          nickname: formData.nickname,
          parent_tel: formData.parent_tel || null,
          school: formData.school || null,
        }]);

      if (error) throw error;

      toast({
        title: 'สำเร็จ',
        description: `เพิ่มนักเรียน ${formData.first_name} ${formData.last_name} (${code}) เรียบร้อยแล้ว`,
      });

      navigate('/admin/students');
    } catch (error) {
      console.error('Error adding student:', error);
      toast({
        title: 'เกิดข้อผิดพลาด',
        description: 'ไม่สามารถเพิ่มนักเรียนได้',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full">
        <AdminSidebar />
        <SidebarInset>
          <div className="container mx-auto px-4 py-8">
            <div className="flex items-center gap-4 mb-6">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/admin/students')}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                กลับ
              </Button>
              <h1 className="text-2xl font-bold">เพิ่มนักเรียนใหม่</h1>
            </div>

            <Card className="max-w-2xl">
              <CardHeader>
                <CardTitle>ข้อมูลนักเรียน</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="first_name">ชื่อ *</Label>
                      <Input
                        id="first_name"
                        value={formData.first_name}
                        onChange={(e) => handleInputChange('first_name', e.target.value)}
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="last_name">นามสกุล *</Label>
                      <Input
                        id="last_name"
                        value={formData.last_name}
                        onChange={(e) => handleInputChange('last_name', e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="nickname">ชื่อเล่น *</Label>
                    <Input
                      id="nickname"
                      value={formData.nickname}
                      onChange={(e) => handleInputChange('nickname', e.target.value)}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="parent_tel">เบอร์โทรผู้ปกครอง</Label>
                    <Input
                      id="parent_tel"
                      value={formData.parent_tel}
                      onChange={(e) => handleInputChange('parent_tel', e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="school">โรงเรียน</Label>
                    <Input
                      id="school"
                      value={formData.school}
                      onChange={(e) => handleInputChange('school', e.target.value)}
                    />
                  </div>

                  <div className="flex gap-4 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => navigate('/admin/students')}
                    >
                      ยกเลิก
                    </Button>
                    <Button type="submit" disabled={isLoading}>
                      {isLoading ? 'กำลังบันทึก...' : 'บันทึก'}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        </SidebarInset>
      </div>
    </SidebarProvider>
  );
};

export default AdminAddStudentPage;